import {
  Button,
  Input,
  Menu,
  MenuItem,
  MenuTrigger,
  Switch,
} from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { ImageIcon, InformationIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';

import {
  PdfSignatureCreator,
  type PdfSignatureCreatorResult,
} from '../../../../../blocksuite/attachment-viewer/pdf/pdf-signature-creator';
import * as pdfStyles from '../../../../../blocksuite/attachment-viewer/pdf/styles.css';
import { useWorkspaceCredentials } from '../credentials/use-workspace-credentials';
import {
  SettingsActionRow,
  SettingsConfirmButton,
  SettingsContentDivider,
  SettingsContentSection,
  SettingsContentSurface,
} from '../shared/settings-content';
import {
  SettingsBadge,
  SettingsEntityList,
  SettingsEntityMessage,
  SettingsExpandableItem,
  SettingsMetaItem,
} from '../shared/settings-entity';
import {
  getPdfSignatureErrorMessage,
  type SignatureAppearanceInfo,
  type SignatureCertificateRefInfo,
  type SignatureCertificateStatus,
  type SignatureProviderInfo,
  type SignatureProviderStatus,
  type SignatureProviderType,
  useWorkspacePdfSignatures,
} from '../signatures/use-workspace-pdf-signatures';
import * as styles from './styles.css';

interface SelectOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

function SettingsSelect<T extends string>({
  value,
  options,
  onChange,
  disabled,
  dataTestId,
}: {
  value: T;
  options: ReadonlyArray<SelectOption<T>>;
  onChange: (value: T) => void;
  disabled?: boolean;
  dataTestId?: string;
}) {
  const current = options.find(option => option.value === value) ?? options[0];
  return (
    <Menu
      contentOptions={{ align: 'end' }}
      items={options.map(option => (
        <MenuItem
          key={option.value}
          disabled={option.disabled}
          onSelect={() => {
            if (!option.disabled) {
              onChange(option.value);
            }
          }}
          data-selected={option.value === value}
        >
          {option.label}
        </MenuItem>
      ))}
    >
      <MenuTrigger
        block
        className={styles.selectTrigger}
        disabled={disabled || options.every(option => option.disabled)}
        data-testid={dataTestId}
      >
        {current?.label ?? ''}
      </MenuTrigger>
    </Menu>
  );
}

function getProviderTypeLabel(
  t: ReturnType<typeof useI18n>,
  type: SignatureProviderType
) {
  switch (type) {
    case 'eu_dss':
      return t[
        'com.streamlain.pdf.signature.settings.admin.providerType.euDss'
      ]();
    case 'open_pdf_sign':
      return t[
        'com.streamlain.pdf.signature.settings.admin.providerType.openPdfSign'
      ]();
    case 'remote_provider':
      return t[
        'com.streamlain.pdf.signature.settings.admin.providerType.remoteProvider'
      ]();
    case 'test':
      return t[
        'com.streamlain.pdf.signature.settings.admin.providerType.test'
      ]();
  }
}

function getProviderStatusLabel(
  t: ReturnType<typeof useI18n>,
  status: SignatureProviderStatus
) {
  switch (status) {
    case 'active':
      return t['com.streamlain.pdf.signature.settings.status.active']();
    case 'disabled':
      return t['com.streamlain.pdf.signature.settings.status.disabled']();
    case 'unavailable':
      return t['com.streamlain.pdf.signature.settings.status.unavailable']();
  }
}

function getCertificateStatusLabel(
  t: ReturnType<typeof useI18n>,
  status: SignatureCertificateStatus
) {
  switch (status) {
    case 'active':
      return t['com.streamlain.pdf.signature.settings.status.active']();
    case 'disabled':
      return t['com.streamlain.pdf.signature.settings.status.disabled']();
    case 'expired':
      return t['com.streamlain.pdf.signature.settings.status.expired']();
    case 'revoked':
      return t['com.streamlain.pdf.signature.settings.status.revoked']();
    case 'untrusted':
      return t['com.streamlain.pdf.signature.settings.status.untrusted']();
  }
}

function statusVariant(
  status: SignatureProviderStatus | SignatureCertificateStatus
) {
  return status === 'active'
    ? 'active'
    : status === 'revoked' ||
        status === 'expired' ||
        status === 'untrusted' ||
        status === 'unavailable'
      ? 'error'
      : 'soft';
}

function ProviderItem({
  provider,
  disabled,
  updateProvider,
}: {
  provider: SignatureProviderInfo;
  disabled?: boolean;
  updateProvider: ReturnType<
    typeof useWorkspacePdfSignatures
  >['updateProvider'];
}) {
  const t = useI18n();
  const [expanded, setExpanded] = useState(false);
  const isActive = provider.status === 'active';

  return (
    <SettingsExpandableItem
      title={provider.name}
      expanded={expanded}
      onToggle={() => setExpanded(value => !value)}
      badges={
        <SettingsBadge variant={statusVariant(provider.status)}>
          {getProviderStatusLabel(t, provider.status)}
        </SettingsBadge>
      }
      meta={
        <>
          <SettingsMetaItem>
            {getProviderTypeLabel(t, provider.type)}
          </SettingsMetaItem>
          <SettingsMetaItem>
            {provider.providerCredentialId
              ? t[
                  'com.streamlain.pdf.signature.settings.admin.providers.credentialLinked'
                ]()
              : t[
                  'com.streamlain.pdf.signature.settings.admin.providers.noCredential'
                ]()}
          </SettingsMetaItem>
        </>
      }
    >
      <SettingsContentSurface>
        <SettingsActionRow>
          <Button
            variant="secondary"
            disabled={disabled}
            onClick={() =>
              void updateProvider(provider.id, {
                status: isActive ? 'disabled' : 'active',
              })
            }
          >
            {isActive
              ? t['com.streamlain.pdf.signature.settings.digital.disable']()
              : t['com.streamlain.pdf.signature.settings.digital.enable']()}
          </Button>
        </SettingsActionRow>
      </SettingsContentSurface>
    </SettingsExpandableItem>
  );
}

function CertificateItem({
  certificate,
  providerName,
  disabled,
  updateCertificate,
}: {
  certificate: SignatureCertificateRefInfo;
  providerName: string;
  disabled?: boolean;
  updateCertificate: ReturnType<
    typeof useWorkspacePdfSignatures
  >['updateCertificate'];
}) {
  const t = useI18n();
  const [expanded, setExpanded] = useState(false);
  const isActive = certificate.status === 'active';
  const isRevoked = certificate.status === 'revoked';

  return (
    <SettingsExpandableItem
      title={certificate.name}
      expanded={expanded}
      onToggle={() => setExpanded(value => !value)}
      badges={
        <SettingsBadge variant={statusVariant(certificate.status)}>
          {getCertificateStatusLabel(t, certificate.status)}
        </SettingsBadge>
      }
      meta={
        <>
          <SettingsMetaItem>{providerName}</SettingsMetaItem>
          <SettingsMetaItem>{certificate.fingerprint}</SettingsMetaItem>
        </>
      }
    >
      <SettingsContentSurface>
        <SettingsContentSection
          title={t[
            'com.streamlain.pdf.signature.settings.admin.certificates.details'
          ]()}
        >
          {certificate.subject ? (
            <div className={styles.mutedText}>
              {t[
                'com.streamlain.pdf.signature.settings.admin.certificates.subject'
              ]()}
              : {certificate.subject}
            </div>
          ) : null}
          {certificate.issuer ? (
            <div className={styles.mutedText}>
              {t[
                'com.streamlain.pdf.signature.settings.admin.certificates.issuer'
              ]()}
              : {certificate.issuer}
            </div>
          ) : null}
        </SettingsContentSection>
        <SettingsContentDivider />
        <SettingsActionRow>
          <Button
            variant="secondary"
            disabled={disabled || isRevoked}
            onClick={() =>
              void updateCertificate(certificate.id, {
                status: isActive ? 'disabled' : 'active',
              })
            }
          >
            {isActive
              ? t['com.streamlain.pdf.signature.settings.digital.disable']()
              : t['com.streamlain.pdf.signature.settings.digital.enable']()}
          </Button>
          <SettingsConfirmButton
            label={t[
              'com.streamlain.pdf.signature.settings.admin.certificates.revoke'
            ]()}
            confirmLabel={t[
              'com.streamlain.pdf.signature.settings.admin.certificates.revokeConfirm'
            ]()}
            cancelLabel={t['com.streamlain.pdf.signature.settings.cancel']()}
            disabled={disabled || isRevoked}
            loading={disabled}
            onConfirm={() =>
              void updateCertificate(certificate.id, { status: 'revoked' })
            }
            variant="secondary"
          />
        </SettingsActionRow>
      </SettingsContentSurface>
    </SettingsExpandableItem>
  );
}

function OrganizationSealPreview({
  seal,
  imageUrl,
}: {
  seal: SignatureAppearanceInfo | null;
  imageUrl: string | null;
}) {
  const t = useI18n();
  if (!seal || !imageUrl) {
    return (
      <div className={pdfStyles.pdfSignatureEmptyState}>
        <ImageIcon aria-hidden />
        <span>
          {t[
            'com.streamlain.pdf.signature.settings.admin.organizationSeal.empty'
          ]()}
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={pdfStyles.pdfSignatureAppearanceCard}
      data-active="true"
      aria-label={seal.name}
      title={seal.name}
    >
      <img alt="" src={imageUrl} />
      <span>{seal.name}</span>
    </button>
  );
}

export function WorkspaceSettingPdfSignatures() {
  const t = useI18n();
  const workspaceService = useService(WorkspaceService);
  const signatures = useWorkspacePdfSignatures();
  const { credentials } = useWorkspaceCredentials();
  const [organizationSealCreatorOpen, setOrganizationSealCreatorOpen] =
    useState(false);
  const [providerName, setProviderName] = useState('');
  const [providerType, setProviderType] =
    useState<SignatureProviderType>('remote_provider');
  const [providerCredentialId, setProviderCredentialId] = useState('');
  const [certificateProviderId, setCertificateProviderId] = useState('');
  const [certificateName, setCertificateName] = useState('');
  const [certificateFingerprint, setCertificateFingerprint] = useState('');
  const [certificateSubject, setCertificateSubject] = useState('');
  const [certificateIssuer, setCertificateIssuer] = useState('');

  const loadErrorMessage = signatures.loadError
    ? getPdfSignatureErrorMessage(signatures.loadError, 'load')
    : null;
  const organizationSeal =
    signatures.appearances.find(
      appearance =>
        appearance.type === 'organization_seal' &&
        appearance.status === 'active'
    ) ?? null;
  const organizationSealUrl = organizationSeal
    ? signatures.appearanceImageUrl(organizationSeal.id)
    : null;
  const providerNameById = useMemo(
    () =>
      new Map(
        signatures.providers.map(provider => [provider.id, provider.name])
      ),
    [signatures.providers]
  );
  const providerOptions = useMemo<Array<SelectOption<string>>>(
    () =>
      signatures.providers.length
        ? signatures.providers.map(provider => ({
            value: provider.id,
            label: provider.name,
          }))
        : [
            {
              value: '',
              label:
                t[
                  'com.streamlain.pdf.signature.settings.admin.providers.empty'
                ](),
              disabled: true,
            },
          ],
    [signatures.providers, t]
  );
  const credentialOptions = useMemo<Array<SelectOption<string>>>(
    () => [
      {
        value: '',
        label:
          t[
            'com.streamlain.pdf.signature.settings.admin.providers.noCredential'
          ](),
      },
      ...credentials.map(credential => ({
        value: credential.id,
        label: credential.name,
      })),
    ],
    [credentials, t]
  );
  const providerTypeOptions = useMemo<
    Array<SelectOption<SignatureProviderType>>
  >(
    () =>
      (
        [
          'remote_provider',
          'eu_dss',
          'open_pdf_sign',
          'test',
        ] as SignatureProviderType[]
      ).map(type => ({
        value: type,
        label: getProviderTypeLabel(t, type),
      })),
    [t]
  );
  const policyAllowedProviderIds = signatures.policy?.allowedProviderIds ?? [];
  const policyEffectiveProviderIds = policyAllowedProviderIds.length
    ? policyAllowedProviderIds
    : signatures.providers.map(provider => provider.id);
  const allowVisualOnlyExport =
    signatures.policy?.allowVisualOnlyExport ?? true;

  const resolveClipboardBlob = useCallback(
    async (blobId: string) =>
      workspaceService.workspace.docCollection.blobSync.get(blobId),
    [workspaceService]
  );

  const uploadOrganizationSeal = useCallback(
    async ({ file }: PdfSignatureCreatorResult) => {
      try {
        await signatures.uploadAppearance({
          name: t[
            'com.streamlain.pdf.signature.settings.admin.organizationSeal.autoName'
          ](),
          type: 'organization_seal',
          file,
        });
        setOrganizationSealCreatorOpen(false);
        return true;
      } catch (error) {
        console.error('Failed to upload PDF organization seal', error);
        return false;
      }
    },
    [signatures, t]
  );

  const createProvider = useCallback(async () => {
    const name = providerName.trim();
    if (!name) return;
    try {
      const provider = await signatures.createProvider({
        name,
        type: providerType,
        providerCredentialId: providerCredentialId || null,
      });
      setProviderName('');
      setProviderCredentialId('');
      setCertificateProviderId(provider.id);
    } catch (error) {
      console.error('Failed to create PDF signing provider', error);
    }
  }, [providerCredentialId, providerName, providerType, signatures]);

  const createCertificate = useCallback(async () => {
    if (
      !certificateProviderId ||
      !certificateName.trim() ||
      !certificateFingerprint.trim()
    ) {
      return;
    }
    try {
      await signatures.createCertificate({
        providerId: certificateProviderId,
        name: certificateName.trim(),
        fingerprint: certificateFingerprint.trim(),
        subject: certificateSubject.trim() || null,
        issuer: certificateIssuer.trim() || null,
      });
      setCertificateName('');
      setCertificateFingerprint('');
      setCertificateSubject('');
      setCertificateIssuer('');
    } catch (error) {
      console.error(
        'Failed to create PDF signing certificate reference',
        error
      );
    }
  }, [
    certificateFingerprint,
    certificateIssuer,
    certificateName,
    certificateProviderId,
    certificateSubject,
    signatures,
  ]);

  const togglePolicyProvider = useCallback(
    (providerId: string) => {
      const current = new Set(policyEffectiveProviderIds);
      if (current.has(providerId)) {
        current.delete(providerId);
      } else {
        current.add(providerId);
      }
      const next = [...current];
      void signatures.updatePolicy({
        allowedProviderIds:
          next.length === signatures.providers.length ? [] : next,
      });
    },
    [policyEffectiveProviderIds, signatures]
  );

  return (
    <>
      <SettingHeader
        title={t['com.streamlain.pdf.signature.settings.admin.title']()}
        subtitle={t['com.streamlain.pdf.signature.settings.admin.subtitle']()}
      />
      <SettingWrapper id="workspace-pdf-signatures">
        {loadErrorMessage ? (
          <SettingRow
            name={t['com.streamlain.pdf.signature.settings.title']()}
            spreadCol={false}
          >
            <div className={pdfStyles.pdfSignatureInlineStatus}>
              <InformationIcon aria-hidden />
              <span>{loadErrorMessage}</span>
            </div>
          </SettingRow>
        ) : null}

        <SettingRow
          name={t[
            'com.streamlain.pdf.signature.settings.admin.organizationSeal.title'
          ]()}
          desc={t[
            'com.streamlain.pdf.signature.settings.admin.organizationSeal.desc'
          ]()}
          spreadCol={false}
        >
          <div className={styles.panel}>
            <OrganizationSealPreview
              seal={organizationSeal}
              imageUrl={organizationSealUrl}
            />
            <button
              type="button"
              className={pdfStyles.pdfSignatureCreateButton}
              aria-expanded={organizationSealCreatorOpen}
              disabled={signatures.isSaving}
              onClick={() => setOrganizationSealCreatorOpen(open => !open)}
            >
              <span>
                {organizationSeal
                  ? t[
                      'com.streamlain.pdf.signature.settings.admin.organizationSeal.replace'
                    ]()
                  : t[
                      'com.streamlain.pdf.signature.settings.admin.organizationSeal.create'
                    ]()}
              </span>
            </button>
            {organizationSealCreatorOpen ? (
              <PdfSignatureCreator
                isSaving={signatures.isSaving}
                onCreate={uploadOrganizationSeal}
                resolveClipboardBlob={resolveClipboardBlob}
              />
            ) : null}
          </div>
        </SettingRow>

        <SettingRow
          name={t[
            'com.streamlain.pdf.signature.settings.admin.providers.title'
          ]()}
          desc={t[
            'com.streamlain.pdf.signature.settings.admin.providers.desc'
          ]()}
          spreadCol={false}
        >
          <div className={styles.panel}>
            <div className={styles.formCard}>
              <div className={styles.formGrid}>
                <Input
                  value={providerName}
                  onChange={setProviderName}
                  placeholder={t[
                    'com.streamlain.pdf.signature.settings.admin.providers.name'
                  ]()}
                />
                <SettingsSelect
                  value={providerType}
                  options={providerTypeOptions}
                  disabled={signatures.isSaving}
                  dataTestId="workspace-pdf-signature-provider-type"
                  onChange={setProviderType}
                />
              </div>
              <SettingsSelect
                value={providerCredentialId}
                options={credentialOptions}
                disabled={signatures.isSaving}
                dataTestId="workspace-pdf-signature-provider-credential"
                onChange={setProviderCredentialId}
              />
              <div className={styles.actionRow}>
                <Button
                  variant="primary"
                  disabled={signatures.isSaving || !providerName.trim()}
                  onClick={() => void createProvider()}
                >
                  {t[
                    'com.streamlain.pdf.signature.settings.admin.providers.save'
                  ]()}
                </Button>
              </div>
            </div>
            {signatures.providers.length > 0 ? (
              <SettingsEntityList>
                {signatures.providers.map(provider => (
                  <ProviderItem
                    key={provider.id}
                    provider={provider}
                    disabled={signatures.isSaving}
                    updateProvider={signatures.updateProvider}
                  />
                ))}
              </SettingsEntityList>
            ) : (
              <SettingsEntityMessage>
                {t[
                  'com.streamlain.pdf.signature.settings.admin.providers.empty'
                ]()}
              </SettingsEntityMessage>
            )}
          </div>
        </SettingRow>

        <SettingRow
          name={t[
            'com.streamlain.pdf.signature.settings.admin.certificates.title'
          ]()}
          desc={t[
            'com.streamlain.pdf.signature.settings.admin.certificates.desc'
          ]()}
          spreadCol={false}
        >
          <div className={styles.panel}>
            <div className={styles.formCard}>
              <div className={styles.formGrid}>
                <SettingsSelect
                  value={certificateProviderId}
                  options={providerOptions}
                  disabled={signatures.isSaving}
                  dataTestId="workspace-pdf-signature-certificate-provider"
                  onChange={setCertificateProviderId}
                />
                <Input
                  value={certificateName}
                  onChange={setCertificateName}
                  placeholder={t[
                    'com.streamlain.pdf.signature.settings.admin.certificates.name'
                  ]()}
                />
              </div>
              <div className={styles.formGrid}>
                <Input
                  value={certificateFingerprint}
                  onChange={setCertificateFingerprint}
                  placeholder={t[
                    'com.streamlain.pdf.signature.settings.admin.certificates.fingerprint'
                  ]()}
                />
                <Input
                  value={certificateSubject}
                  onChange={setCertificateSubject}
                  placeholder={t[
                    'com.streamlain.pdf.signature.settings.admin.certificates.subject'
                  ]()}
                />
              </div>
              <Input
                value={certificateIssuer}
                onChange={setCertificateIssuer}
                placeholder={t[
                  'com.streamlain.pdf.signature.settings.admin.certificates.issuer'
                ]()}
              />
              <div className={styles.actionRow}>
                <Button
                  variant="primary"
                  disabled={
                    signatures.isSaving ||
                    !certificateProviderId ||
                    !certificateName.trim() ||
                    !certificateFingerprint.trim()
                  }
                  onClick={() => void createCertificate()}
                >
                  {t[
                    'com.streamlain.pdf.signature.settings.admin.certificates.save'
                  ]()}
                </Button>
              </div>
            </div>
            {signatures.certificates.length > 0 ? (
              <SettingsEntityList>
                {signatures.certificates.map(certificate => (
                  <CertificateItem
                    key={certificate.id}
                    certificate={certificate}
                    providerName={
                      providerNameById.get(certificate.providerId) ??
                      t[
                        'com.streamlain.pdf.signature.digital.providerFallback'
                      ]()
                    }
                    disabled={signatures.isSaving}
                    updateCertificate={signatures.updateCertificate}
                  />
                ))}
              </SettingsEntityList>
            ) : (
              <SettingsEntityMessage>
                {t[
                  'com.streamlain.pdf.signature.settings.admin.certificates.empty'
                ]()}
              </SettingsEntityMessage>
            )}
          </div>
        </SettingRow>

        <SettingRow
          name={t['com.streamlain.pdf.signature.settings.admin.policy.title']()}
          desc={t['com.streamlain.pdf.signature.settings.admin.policy.desc']()}
          spreadCol={false}
        >
          <div className={styles.panel}>
            <SettingsContentSurface>
              <SettingsContentSection
                title={t[
                  'com.streamlain.pdf.signature.settings.admin.policy.visualOnly'
                ]()}
              >
                <div className={styles.policyList}>
                  <div className={styles.policyRow}>
                    <div className={styles.policyRowText}>
                      <div className={styles.policyRowTitle}>
                        {t[
                          'com.streamlain.pdf.signature.settings.admin.policy.visualOnly'
                        ]()}
                      </div>
                      <div className={styles.policyRowMeta}>
                        {t[
                          'com.streamlain.pdf.signature.settings.admin.policy.visualOnlyDesc'
                        ]()}
                      </div>
                    </div>
                    <Switch
                      checked={allowVisualOnlyExport}
                      disabled={signatures.isSaving}
                      onChange={checked =>
                        void signatures.updatePolicy({
                          allowVisualOnlyExport: checked,
                        })
                      }
                    />
                  </div>
                </div>
              </SettingsContentSection>
              <SettingsContentDivider />
              <SettingsContentSection
                title={t[
                  'com.streamlain.pdf.signature.settings.admin.policy.allowedProviders'
                ]()}
              >
                <div className={styles.mutedText}>
                  {policyAllowedProviderIds.length === 0
                    ? t[
                        'com.streamlain.pdf.signature.settings.admin.policy.allProvidersAllowed'
                      ]()
                    : t[
                        'com.streamlain.pdf.signature.settings.admin.policy.allowedProvidersDesc'
                      ]()}
                </div>
                {signatures.providers.length > 0 ? (
                  <div className={styles.policyList}>
                    {signatures.providers.map(provider => (
                      <div key={provider.id} className={styles.policyRow}>
                        <div className={styles.policyRowText}>
                          <div className={styles.policyRowTitle}>
                            {provider.name}
                          </div>
                          <div className={styles.policyRowMeta}>
                            {getProviderTypeLabel(t, provider.type)}
                          </div>
                        </div>
                        <Switch
                          checked={policyEffectiveProviderIds.includes(
                            provider.id
                          )}
                          disabled={signatures.isSaving}
                          onChange={() => togglePolicyProvider(provider.id)}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <SettingsEntityMessage>
                    {t[
                      'com.streamlain.pdf.signature.settings.admin.policy.noProviders'
                    ]()}
                  </SettingsEntityMessage>
                )}
              </SettingsContentSection>
            </SettingsContentSurface>
          </div>
        </SettingRow>
      </SettingWrapper>
    </>
  );
}
